import { css, type StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { XIconNew } from '../../../web/components/svgs/icons/XIcon';
import { Button } from '../../../web/components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import {
	getFilterOptionKey,
	type InboxFilterOption,
	type InboxFilters,
	type InboxSort,
	PRIORITY_FILTER_OPTIONS,
	STATUS_FILTER_OPTIONS,
} from './inboxFilters';
import { inboxStyles as s } from './styles/inboxStyles';
import { TicketFilterCsmRow } from './TicketFilterCsmRow';

const SORT_OPTIONS: ReadonlyArray<InboxFilterOption<InboxSort | undefined>> = [
	{ key: 'desk', value: undefined, label: 'Inbox order' },
	{ key: 'newest', value: 'newest', label: 'Newest first' },
	{ key: 'oldest', value: 'oldest', label: 'Oldest first' },
	{ key: 'priority', value: 'priority', label: 'Priority' },
];

interface FilterSelectRowProps<T> {
	label: string;
	value: T;
	/** The domain value that represents "no filter"; it renders muted like a placeholder. */
	inactiveValue: T;
	options: ReadonlyArray<InboxFilterOption<T>>;
	onValueChange: (value: T) => void;
}

/**
 * One labelled Select row (label left, current value + chevron right). Reuses the v2 Select so the
 * option list, keyboard nav, and selected-state styling come for free.
 */
function FilterSelectRow<T>({ label, value, inactiveValue, options, onValueChange }: FilterSelectRowProps<T>) {
	const selectedKey = getFilterOptionKey(options, value);
	return (
		<div className={css(s.filterRow)}>
			<Select
				value={selectedKey}
				onValueChange={key => {
					const nextOption = options.find(option => option.key === key);
					if (nextOption) onValueChange(nextOption.value);
				}}
			>
				<SelectTrigger styles={s.filterRowTrigger} aria-label={label}>
					<span className={css(s.filterRowLabel)}>{label}</span>
					<SelectValue
						render={(_, selected) => {
							const selectedOption = options.find(option => option.key === selected.value);
							return (
								<span className={css(s.filterRowValue, !Object.is(value, inactiveValue) && s.filterRowValueActive)}>
									{selectedOption?.label ?? options[0]?.label}
								</span>
							);
						}}
					/>
				</SelectTrigger>
				<SelectContent>
					{options.map(option => (
						<SelectItem
							key={option.key}
							value={option.key}
							hint={option.hint !== undefined ? <span title={option.hintTitle}>{option.hint}</span> : undefined}
						>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
}

interface TicketFilterPanelProps {
	filters: InboxFilters;
	onFiltersChange: (next: InboxFilters) => void;
	categoryOptions: ReadonlyArray<InboxFilterOption<string | undefined>>;
	assigneeOptions: ReadonlyArray<InboxFilterOption<string | undefined>>;
	/** Search endpoint results keep the server's recency ordering and cannot be explicitly sorted. */
	allowSort: boolean;
	onClearAll: () => void;
	onClose: () => void;
	panelRef?: React.Ref<HTMLDivElement>;
	/** Placement overrides for hosts that anchor the popover somewhere other than the desk bar. */
	styles?: StyleDeclarationValue[];
}

/**
 * The "Search criteria" popover body: a stacked set of Select rows that narrow the current view or
 * search results, with Clear all / Done in the footer. Filter/sort state is owned by the inbox
 * container; this component is a controlled view over it.
 */
export const TicketFilterPanel: React.FC<TicketFilterPanelProps> = ({
	filters,
	onFiltersChange,
	categoryOptions,
	assigneeOptions,
	allowSort,
	onClearAll,
	onClose,
	panelRef,
	styles = [],
}) => {
	const setFilter = (patch: Partial<InboxFilters>) => onFiltersChange({ ...filters, ...patch });

	return (
		<div ref={panelRef} className={css(s.filterPanel, ...styles)} role='dialog' aria-label='Search criteria'>
			<div className={css(s.filterPanelHeader)}>
				<span className={css(s.filterPanelTitle)}>Search criteria</span>
				<Button
					type='button'
					variant='ghost'
					styles={[s.filterPanelClose]}
					aria-label='Close search criteria'
					onClick={onClose}
				>
					<XIconNew width={15} height={15} />
				</Button>
			</div>

			<div className={css(s.filterPanelBody)}>
				{allowSort && (
					<FilterSelectRow<InboxSort | undefined>
						label='Sort'
						value={filters.sort}
						inactiveValue={undefined}
						options={SORT_OPTIONS}
						onValueChange={sort => setFilter({ sort })}
					/>
				)}
				<FilterSelectRow
					label='Status'
					value={filters.status}
					inactiveValue={undefined}
					options={STATUS_FILTER_OPTIONS}
					onValueChange={status => setFilter({ status })}
				/>
				<FilterSelectRow
					label='Priority'
					value={filters.priority}
					inactiveValue={undefined}
					options={PRIORITY_FILTER_OPTIONS}
					onValueChange={priority => setFilter({ priority })}
				/>
				<FilterSelectRow
					label='Category'
					value={filters.categoryId}
					inactiveValue={undefined}
					options={categoryOptions}
					onValueChange={categoryId => setFilter({ categoryId })}
				/>
				<FilterSelectRow
					label='Assignee'
					value={filters.assigneeId}
					inactiveValue={undefined}
					options={assigneeOptions}
					onValueChange={assigneeId => setFilter({ assigneeId })}
				/>
				<TicketFilterCsmRow
					csmId={filters.csmId}
					csmName={filters.csmName}
					onChange={csm => setFilter({ csmId: csm?.id, csmName: csm?.name })}
				/>
			</div>

			<div className={css(s.filterPanelFooter)}>
				<Button type='button' variant='ghost' styles={[s.filterClearBtn]} onClick={onClearAll}>
					Clear all
				</Button>
				<Button type='button' variant='primary' styles={[s.filterDoneBtn]} onClick={onClose}>
					Done
				</Button>
			</div>
		</div>
	);
};
